import { Badge, Link, useTheme } from "@mui/material";
import {
    IItemOptions,
    INavigation,
} from "Layouts/SideNavigation/NavigationStore";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ThemeColors } from "Theme/AppTheme";
import hexToRGB from "utils/hexToRGB";
import { styleClsx, sxClsx } from "utils/styleClsx";

type Props = {
    val: INavigation;
    itemOptions?: IItemOptions;
    currentNavItem?: string;
    isActive?: boolean; // Takes precedence over currentNavItem if provided
    replaceLinks: {
        [key: string]: string;
    };
    onClick?: () => void;
};

const SideNavigationLink: React.FC<Props> = observer((props: Props) => {
    const { val, itemOptions, currentNavItem, replaceLinks, onClick } = props;
    if (val.matchSubpaths === undefined) {
        val.matchSubpaths = false; // Default to false for backward compatibility
    }
    const [link, setLink] = useState<string>("");
    const theme = useTheme();
    useEffect(() => {
        setLink(
            Object.keys(replaceLinks).reduce((str, param) => {
                str = str.replace(`:${param}`, replaceLinks[param]);
                return str;
            }, val.link),
        );
    }, [replaceLinks, val]);

    const isActiveRoute = (route: string) => {
        if (props.isActive !== undefined) {
            return props.isActive;
        }
        if (!currentNavItem) return false;
        return (
            currentNavItem === link ||
            (val.matchSubpaths && currentNavItem.startsWith(link + "/"))
        );
    };

    return (
        <div
            key={`config-links-${val.title}`}
            style={styleClsx(
                isActiveRoute(link) && {
                    borderRadius: "5px",
                    backgroundColor: hexToRGB(ThemeColors.blue, 0.1),
                },
                {
                    width: "100%",
                    paddingLeft: "6px",
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    minHeight: "32px",
                    marginTop: theme.spacing(1),
                    marginBottom: theme.spacing(1),
                },
                itemOptions?.itemSpacing === "spread" && {
                    marginTop: theme.spacing(1.5),
                    marginBottom: theme.spacing(1.5),
                },
                itemOptions?.itemSpacing === "dense" && {
                    marginTop: theme.spacing(0.5),
                    marginBottom: theme.spacing(0.5),
                },
            )}
        >
            {val.badgeContent || val.icon || val.selectedIcon ? (
                <div
                    style={styleClsx(
                        {
                            float: "left",
                            height: "24px",
                            width: "24px",
                            display: "flex",
                            alignItems: "center",
                        },
                        !isActiveRoute(link) && { opacity: 0.5 },
                    )}
                >
                    {val.badgeContent ? (
                        <Badge
                            max={9999}
                            color="primary"
                            variant="dot"
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            badgeContent={val.badgeContent}
                            {...val.badgeOptions}
                        >
                            <>
                                {isActiveRoute(link) && val.selectedIcon
                                    ? val.selectedIcon
                                    : val.icon}
                            </>
                        </Badge>
                    ) : isActiveRoute(link) && val.selectedIcon ? (
                        val.selectedIcon
                    ) : (
                        val.icon
                    )}
                </div>
            ) : (
                // spacer for items without icon alignment
                <div
                    style={{
                        float: "left",
                        height: "24px",
                        width: "4px",
                    }}
                ></div>
            )}
            <div
                key={`link-container-${val.title}`}
                style={{
                    paddingLeft: "6px",
                    marginLeft: "8px",
                    paddingRight: "6px",
                    fontSize: props.val.signalsVariant ? "12px" : "16px",
                    minHeight: props.val.removeMinHeight ? "0" : "24px",
                    display: props.val.signalsVariant ? "flex" : "block",
                    alignItems: props.val.signalsVariant ? "center" : undefined,
                }}
            >
                {val.enabled === false ? (
                    <Link
                        component="button"
                        disabled
                        color="textPrimary"
                        underline="hover"
                        style={{
                            textAlign: "left",
                            textDecoration: "none",
                            cursor: "auto",
                            opacity: "50%",
                            // Need this extra control due to signals nav being a combination of old and new nav due to legacy reports code
                            fontSize: props.val.signalsVariant
                                ? "12px"
                                : itemOptions?.linkFontSize ?? "16px",
                        }}
                        variant="body1"
                        key={`navigation-item-${val.title}`}
                        sx={sxClsx(
                            !isActiveRoute(link) && {
                                color: hexToRGB(ThemeColors.black, 0.5),
                            },

                            isActiveRoute(link) && {
                                color: ThemeColors.gray,
                                fontWeight: "bold",
                                textAlign: "left",
                            },
                        )}
                        onClick={onClick}
                    >
                        {val.title}
                    </Link>
                ) : (
                    <Link
                        component={RouterLink}
                        to={link}
                        color="textPrimary"
                        underline="hover"
                        variant="body1"
                        key={`navigation-item-${val.title}`}
                        sx={sxClsx({
                            color: ThemeColors.black,
                            textAlign: "left",
                            // Need this extra control due to signals nav being a combination of old and new nav due to legacy reports code
                            fontSize: props.val.signalsVariant
                                ? "12px"
                                : itemOptions?.linkFontSize ?? "16px",
                        })}
                        onClick={onClick}
                    >
                        {val.title}
                    </Link>
                )}
            </div>
        </div>
    );
});

export default SideNavigationLink;
